import {
  Banner,
  BarChart,
  Button,
  Card,
  LineChart,
  Skeleton,
  Stat,
} from '@servicecompressionllc/sc-design-system';
import { Download } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { formatCompact, formatCount, formatUsd } from '../../lib/format.js';
import { PeriodPicker } from './PeriodPicker.js';
import { CacheShareCard, CostPerAnswerCard, MonthCard } from './UsageCostCards.js';
import { MakeupCard, SpendByModelCard, SpendBySourceCard } from './UsageSpendCards.js';
import { ExpensiveConversationsCard, SourcesCard, ToolsCard, UsersCard } from './UsageTables.js';
import { downloadUsersCsv } from './usageCsv.js';
import { periodCaption, periodHeading, resolvedRange, usageErrorMessage } from './usageLabels.js';
import { labeledTrend } from './usageTrend.js';
import { useUsageLogic } from './useUsageLogic.js';

export const UsagePage = () => {
  const { period, setPeriod, isLoading, data, error, conversations, conversationsError } =
    useUsageLogic();
  const hourly = period.kind === 'preset' && period.period === '24h';
  const trend = labeledTrend((hourly ? data?.byHour : data?.byDay) ?? [], hourly);
  const caption = periodCaption(period);

  return (
    <AppShell>
      <div className="relative flex min-w-0 flex-col gap-6">
        <section className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-ink">Usage</h2>
            <p className="text-sm text-ink-muted">
              Assistant adoption, activity, top tools, and what it costs.
              {data ? ` ${resolvedRange(data.since, data.until)}.` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PeriodPicker period={period} onChange={setPeriod} />
            <Button
              size="sm"
              variant="secondary"
              disabled={!data}
              onClick={() => {
                if (data) downloadUsersCsv(data);
              }}
            >
              <span className="flex items-center gap-1.5">
                <Download size={14} aria-hidden="true" />
                Export CSV
              </span>
            </Button>
          </div>
        </section>

        {error ? (
          <Banner variant="danger">
            <Banner.Body>
              <Banner.Title>Could not load usage.</Banner.Title>
              <Banner.Description>{usageErrorMessage(error)}</Banner.Description>
            </Banner.Body>
          </Banner>
        ) : isLoading || !data ? (
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-4"
            role="status"
            aria-label="Loading usage"
          >
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : (
          <>
            <section className="grid grid-cols-[repeat(auto-fit,minmax(12.5rem,1fr))] gap-4">
              <Stat
                label="Active users"
                value={formatCount(data.totals.activeUsers)}
                sub={caption}
              />
              <Stat label="Turns" value={formatCount(data.totals.turns)} sub={caption} />
              <Stat
                label="Tokens"
                value={formatCompact(data.totals.totalTokens)}
                sub={`${formatCompact(data.totals.inputTokens)} in · ${formatCompact(data.totals.outputTokens)} out`}
              />
              <Stat
                label="Est. cost"
                value={formatUsd(data.totals.estimatedCostUsd)}
                sub="priced per turn at its model's rates"
              />
            </section>

            <section
              aria-label="Growth"
              className="grid grid-cols-[repeat(auto-fit,minmax(min(26.25rem,100%),1fr))] gap-6"
            >
              <Card padding="md">
                <div className="flex min-w-0 flex-col gap-3">
                  <h3 className="text-sm font-semibold text-ink">
                    {hourly ? 'Active users per hour' : 'Daily active users'}
                  </h3>
                  <BarChart
                    data={trend}
                    xKey="label"
                    yKey="activeUsers"
                    height={200}
                    ariaLabel={hourly ? 'Active users per hour' : 'Daily active users'}
                    emptyMessage="No activity in this period."
                  />
                </div>
              </Card>
              <Card padding="md">
                <div className="flex min-w-0 flex-col gap-3">
                  <h3 className="text-sm font-semibold text-ink">
                    {hourly ? 'Turns per hour' : 'Turns per day'}
                  </h3>
                  <LineChart
                    data={trend}
                    xKey="label"
                    yKey="turns"
                    height={200}
                    ariaLabel={hourly ? 'Turns per hour' : 'Turns per day'}
                    emptyMessage="No activity in this period."
                  />
                </div>
              </Card>
            </section>

            <h3 className="mt-2 text-[1.0625rem] font-semibold text-ink">Cost</h3>

            <section className="grid grid-cols-[repeat(auto-fit,minmax(min(18.75rem,100%),1fr))] gap-4">
              <MonthCard month={data.month} />
              <CostPerAnswerCard data={data} period={period} />
              <CacheShareCard data={data} period={period} />
            </section>

            <section className="grid grid-cols-[repeat(auto-fit,minmax(min(26.25rem,100%),1fr))] gap-6">
              <SpendBySourceCard spend={data.spend} />
              <SpendByModelCard spend={data.spend} />
            </section>

            <SourcesCard sources={data.spend.bySource} />

            <ExpensiveConversationsCard
              rows={conversations?.conversations}
              heading={periodHeading(period)}
              error={conversationsError}
            />

            <MakeupCard spend={data.spend} />

            <UsersCard key={`${data.since}|${data.until}`} users={data.byUser} />

            <section className="grid grid-cols-[repeat(auto-fit,minmax(min(26.25rem,100%),1fr))] gap-6">
              <ToolsCard key={`${data.since}|${data.until}`} tools={data.byTool} />
            </section>

            <p className="text-xs text-ink-subtle">
              Spend is estimated from list prices per model, with cache reads and writes priced
              separately, and stored when each call runs. It covers chat, history summaries, chat
              titles, workflows, reports, feedback triage and admin helpers; the knowledge engine
              isn&apos;t counted. Compare it with the SC Platform workspace in Anthropic&apos;s
              console.
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
};
